import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { I } from "../icons";

const COLORS = ["#4ade80", "#60a5fa", "#f59e0b", "#ef4444", "#a78bfa", "#ec4899"];

interface Props {
  adminOrders: any[];
  filteredOrders: any[];
  P: any[];
  lang: string;
  t: any;
  $: (n: number) => string;
  mapStatus: (s: string) => string;
  dateFrom: string;
  dateTo: string;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  onExportCSV: () => void;
}

export function AdminOverview({ adminOrders, filteredOrders, P, lang, t, $, mapStatus, dateFrom, dateTo, setDateFrom, setDateTo, onExportCSV }: Props) {
  const revenueByDay = useMemo(() => {
    const map: any = {};
    filteredOrders.forEach(o => {
      const d = o.date || "unknown";
      map[d] = (map[d] || 0) + o.total;
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).map(([date, total]: any) => ({ date, total: Math.round(total) }));
  }, [filteredOrders]);

  const ordersByStatus = useMemo(() => {
    const map: any = {};
    filteredOrders.forEach(o => { const st = mapStatus(o.status); map[st] = (map[st] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filteredOrders, mapStatus]);

  const topProducts = useMemo(() => {
    const map: any = {};
    filteredOrders.forEach(o => {
      (o.items || []).forEach((item: any) => {
        const prod = P.find(p => p.dbId === item.product_id || p.id === item.product_id);
        const name = prod ? (typeof prod.nm === "object" ? prod.nm[lang] || prod.nm.en : prod.nm) : item.product_id?.slice(0, 8);
        const rev = (item.price_per_item || 0) * (item.quantity || 1);
        map[name] = (map[name] || 0) + rev;
      });
    });
    return Object.entries(map).sort((a: any, b: any) => b[1] - a[1]).slice(0, 6).map(([name, revenue]: any) => ({ name: name?.length > 18 ? name.slice(0, 16) + "…" : name, revenue: Math.round(revenue) }));
  }, [filteredOrders, P, lang]);

  const totalRevenue = filteredOrders.reduce((s, o) => s + o.total, 0);
  const activeOrders = filteredOrders.filter(o => !["finished", "pending"].includes(o.status)).length;
  const avgOrder = filteredOrders.length ? totalRevenue / filteredOrders.length : 0;

  return (<>
    {/* Date Filters */}
    <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 11, color: "var(--tx3)", fontWeight: 600 }}>{lang === "es" ? "Desde" : "From"}:</label>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 11, color: "var(--tx3)", fontWeight: 600 }}>{lang === "es" ? "Hasta" : "To"}:</label>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)", fontFamily: "var(--f)" }} />
      </div>
      {(dateFrom || dateTo) && (
        <button className="btn bg bs" style={{ fontSize: 10 }} onClick={() => { setDateFrom(""); setDateTo(""); }}>
          {lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      <div style={{ flex: 1 }} />
      <button className="btn bg bs" style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4 }} onClick={onExportCSV}>
        {I.pkg}<span>{lang === "es" ? "Exportar CSV" : "Export CSV"}</span>
      </button>
    </div>

    {/* Stats */}
    <div className="ast">
      <div className="as">
        <div className="lbl">{t.revenue}</div>
        <div className="val">{$(totalRevenue)}</div>
        {filteredOrders.length !== adminOrders.length && <div style={{ fontSize: 9, color: "var(--tx3)" }}>{filteredOrders.length} {lang === "es" ? "órdenes filtradas" : "filtered orders"}</div>}
      </div>
      <div className="as"><div className="lbl">{t.activeOrders}</div><div className="val">{activeOrders}</div></div>
      <div className="as"><div className="lbl">{lang === "es" ? "Promedio/Orden" : "Avg/Order"}</div><div className="val">{$(avgOrder)}</div></div>
      <div className="as"><div className="lbl">{lang === "es" ? "Total Órdenes" : "Total Orders"}</div><div className="val">{filteredOrders.length}</div></div>
    </div>

    {/* Charts */}
    {filteredOrders.length > 0 && (
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
        <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: "14px 10px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 10, color: "var(--tx2)" }}>{lang === "es" ? "📊 Ingresos por Día" : "📊 Revenue by Day"}</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={revenueByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={(v: any) => [`RD$${v.toLocaleString()}`, lang === "es" ? "Ingreso" : "Revenue"]} labelStyle={{ fontSize: 10 }} contentStyle={{ fontSize: 11, background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 8 }} />
              <Bar dataKey="total" fill="#4ade80" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: "14px 10px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 10, color: "var(--tx2)" }}>{lang === "es" ? "📋 Estado de Órdenes" : "📋 Order Status"}</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={ordersByStatus} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                {ordersByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: "var(--r2)", padding: "14px 10px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 10, color: "var(--tx2)" }}>{lang === "es" ? "🏆 Top Productos" : "🏆 Top Products"}</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={topProducts} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
              <XAxis type="number" tick={{ fontSize: 8 }} tickFormatter={v => `$${v}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} width={70} />
              <Tooltip formatter={(v: any) => [`RD$${v.toLocaleString()}`, lang === "es" ? "Ingreso" : "Revenue"]} contentStyle={{ fontSize: 11, background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 8 }} />
              <Bar dataKey="revenue" fill="#60a5fa" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}
  </>);
}